import { TIntegrationCreatePayloadV2 } from "../../../../../services/api-integrations";
import {
  TFourDEMRSetup,
  TIntegrationCatalogRow,
  TMetaPixelSetup,
  TPipedriveSetup,
  TWebhookSetup,
} from "../types";

type TAdapterContext = {
  selectedIntegration: TIntegrationCatalogRow;
  pipedrive: TPipedriveSetup;
  fourDEMR: TFourDEMRSetup;
  metaPixel: TMetaPixelSetup;
  webhook: TWebhookSetup;
};

type TAdapterResult = {
  payload?: TIntegrationCreatePayloadV2;
  errors: string[];
};

const toOptionalInt = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = parseInt(trimmed, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toOptionalIntList = (value: string): number[] | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const list = trimmed
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => parseInt(item, 10));

  if (list.some((num) => Number.isNaN(num))) {
    return undefined;
  }

  return list;
};

const buildPipedrivePayload = ({
  selectedIntegration,
  pipedrive,
}: TAdapterContext): TAdapterResult => {
  const errors: string[] = [];
  const type = selectedIntegration.integration_type;
  const credentials: Record<string, unknown> = {};

  if (type === "pipedrive_api_key") {
    if (!pipedrive.api_key.trim()) {
      errors.push("API Key is required.");
    } else {
      credentials.api_key = pipedrive.api_key.trim();
    }
  } else if (type === "pipedrive_oauth") {
    if (!pipedrive.client_id.trim()) {
      errors.push("Client ID is required.");
    }
    if (!pipedrive.client_secret.trim()) {
      errors.push("Client Secret is required.");
    }
    credentials.client_id = pipedrive.client_id.trim();
    credentials.client_secret = pipedrive.client_secret.trim();
  } else {
    errors.push(`Unsupported Pipedrive integration type: ${type}`);
  }

  if (errors.length > 0) {
    return { errors };
  }

  const config: Record<string, unknown> = {
    create_as: pipedrive.create_as,
  };

  if (pipedrive.create_as === "deal") {
    const pipelineId = toOptionalInt(pipedrive.pipeline_id);
    const stageId = toOptionalInt(pipedrive.stage_id);
    const userId = toOptionalInt(pipedrive.user_id);

    if (pipedrive.pipeline_id.trim() && pipelineId === undefined) {
      errors.push("Pipeline ID must be a number.");
    }
    if (pipedrive.stage_id.trim() && stageId === undefined) {
      errors.push("Stage ID must be a number.");
    }
    if (pipedrive.user_id.trim() && userId === undefined) {
      errors.push("User ID must be a number.");
    }

    if (pipelineId !== undefined) config.pipeline_id = pipelineId;
    if (stageId !== undefined) config.stage_id = stageId;
    if (userId !== undefined) config.user_id = userId;
  }

  if (errors.length > 0) {
    return { errors };
  }

  return {
    payload: {
      integration_type: type,
      credentials,
      config,
      is_active: true,
    },
    errors,
  };
};

const buildWebhookPayload = ({
  selectedIntegration,
  webhook,
}: TAdapterContext): TAdapterResult => {
  const errors: string[] = [];

  if (!webhook.webhook_url.trim()) {
    errors.push("Destination URL is required.");
  }

  const headers = webhook.headers.reduce<Record<string, string>>((acc, row) => {
    const key = row.key.trim();
    if (!key) return acc;
    acc[key] = row.value;
    return acc;
  }, {});

  const mappings = webhook.mappings.map((row) => ({
    target_field: row.target_field.trim(),
    source_path: row.source_path.trim() || null,
    default_value: row.default_value.trim() || null,
    literal_value: row.literal_value.trim() || null,
    include_if_none: row.include_if_none,
  }));

  if (!mappings.length) {
    errors.push("At least one mapping row is required.");
  }

  if (errors.length > 0) {
    return { errors };
  }

  return {
    payload: {
      integration_type: selectedIntegration.integration_type,
      credentials: null,
      config: {
        webhook_url: webhook.webhook_url.trim(),
        http_method: webhook.http_method,
        headers: Object.keys(headers).length ? headers : undefined,
        field_mappings: mappings,
      },
      is_active: true,
    },
    errors,
  };
};

const buildDrChronoPayload = (): TAdapterResult => ({
  errors: ["DrChrono requires assisted setup. Use Request Custom Integration."],
});

const buildGoHighLevelPayload = (): TAdapterResult => ({
  errors: ["GoHighLevel is coming soon in Integrations V2."],
});

const buildFourDEMRPayload = ({
  selectedIntegration,
  fourDEMR,
}: TAdapterContext): TAdapterResult => {
  const errors: string[] = [];

  if (!fourDEMR.client_id.trim()) {
    errors.push("Client ID is required.");
  }

  if (!fourDEMR.client_secret.trim()) {
    errors.push("Client Secret is required.");
  }

  if (!fourDEMR.subscription_key.trim()) {
    errors.push("Subscription Key is required.");
  }

  const documentTypeId = toOptionalInt(fourDEMR.document_type_id);
  if (fourDEMR.document_type_id.trim() && documentTypeId === undefined) {
    errors.push("Document Type ID must be a number.");
  }

  const providerIds = toOptionalIntList(fourDEMR.provider_ids_csv);
  if (fourDEMR.provider_ids_csv.trim() && providerIds === undefined) {
    errors.push("Provider IDs must be a comma-separated list of numbers.");
  }

  if (errors.length > 0) {
    return { errors };
  }

  return {
    payload: {
      integration_type: selectedIntegration.integration_type,
      credentials: {
        client_id: fourDEMR.client_id.trim(),
        client_secret: fourDEMR.client_secret.trim(),
        subscription_key: fourDEMR.subscription_key.trim(),
      },
      config: {
        create_form_submissions_as: fourDEMR.create_form_submissions_as,
        document_type_id: documentTypeId,
        provider_ids: providerIds ?? [],
      },
      is_active: true,
      auto_sync_notes: true,
    },
    errors,
  };
};

const buildMetaPixelPayload = ({
  selectedIntegration,
  metaPixel,
}: TAdapterContext): TAdapterResult => {
  const errors: string[] = [];
  const pixelId = metaPixel.pixel_id.trim();
  const accessToken = metaPixel.access_token.trim();

  if (!pixelId) {
    errors.push("Meta Dataset ID is required.");
  } else if (!/^\d{5,32}$/.test(pixelId)) {
    errors.push("Meta Dataset ID must contain 5 to 32 digits.");
  }

  if (!accessToken) {
    errors.push("Conversions API access token is required.");
  }

  if (errors.length > 0) {
    return { errors };
  }

  return {
    payload: {
      integration_type: selectedIntegration.integration_type,
      credentials: {
        access_token: accessToken,
      },
      config: {
        pixel_id: pixelId,
        track_page_view: metaPixel.track_page_view,
      },
      is_active: true,
      auto_sync_notes: false,
    },
    errors,
  };
};

export const buildCreateIntegrationPayload = (
  context: TAdapterContext,
): TAdapterResult => {
  const integrationType =
    context.selectedIntegration.integration_type.toLowerCase();

  if (integrationType === "webhook") {
    return buildWebhookPayload(context);
  }

  if (integrationType.startsWith("pipedrive_")) {
    return buildPipedrivePayload(context);
  }

  if (integrationType === "drchrono") {
    return buildDrChronoPayload();
  }

  if (integrationType.includes("gohighlevel")) {
    return buildGoHighLevelPayload();
  }

  if (integrationType === "4demr") {
    return buildFourDEMRPayload(context);
  }

  if (integrationType === "meta_pixel") {
    return buildMetaPixelPayload(context);
  }

  return {
    errors: [
      `Integration type ${context.selectedIntegration.integration_type} is not available in Integrations V2 yet.`,
    ],
  };
};
